import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_ITEMS, FLEX_CONTAINER, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, FONT_WEIGHT, H_100, MIN_WIDTH_100, TEXT_WRAPPING, W_100, W_AUTO } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, KeyboardEventType, KeyboardInput, createHtmlElement, setHTMLElementContent, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { DomTools } from "@cervval.js/client-html5-core/tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { DataEmptyValue } from "@cervval.js/common/src/forms/dataSource";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { asAnyObject } from "@cervval.js/common/src/tools/any.tools";
import { arraysEqualIgnoreOrder, filterNotDefined, wrapAsArray } from "@cervval.js/common/src/tools/collection.array.tools";
import { buildArrayMapWithFunc, buildObjectMapWithFunc } from "@cervval.js/common/src/tools/collection.map.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { ValueOrFunction, anyReallyAny, clone, compare, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { FilterOptions } from "@cervval.js/common/src/tools/string.tools";
import { RowUid } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { CjsEllipsis } from "../ellipsis/ellipsis";
import { EmptyStateDisplayPreConfig } from "../emptyStateDisplay/emptyStateDisplay.types";
import { PopupPosition, ShowPopupParams, ShowPopupReturn, ShowPopupStyling, defaultCssPopup, showPopup } from "../showPopup/showPopup";
import { CjsSmartList, SmartListOnClickEvent, SmartListOptions } from "../smartList/smartList";
import { SELECTION_TYPE } from "../smartTable/selectionModel/smartTableSelectionModel";
import { SEARCH_FILTER, SINGLE_COLUMN_UID } from "../smartTable/smartTable";
import { SmartTableCheckboxSelectorColumn } from "../smartTable/smartTableCheckboxSelectorColumn";
import { SmartTableCellGetValue } from "../smartTable/smartTableColumn.tools";
import { I18nType, i18n } from "./i18n/i18n";

/** Used to grey out the item in the list */
export const SMART_SELECT_ITEM_ELEMENT = "smart-select-item-element";
export const UNSELECTED_LIST_CLASS = "unselected-list";
export const SECTION_ITEM_TD_CLASS = "section-item-td";
export const SECTION_ITEM_CLASS = "section-item";


/** 
 * Get "type" value of given type, if given type if an array, return type of array
 * ex:
 * ValueType<Car> => Car
 * ValueType<Car[]> => Car
 * ValueType<(string|number)[]> => string|number
 */
export type ValueType<T> = T extends anyReallyAny[] ? T[number] : T;

/** Remove non-overridable options of showPopupParams for smartSelect */
type SmartSelectPopupOptions = Omit<ShowPopupParams, "elementToShow" | "event">;
/** When updating selection, indicates whether passed items must be merged with current selection, or override it */
type SmartSelectUpdateType = "merge" | "replace";
export type SmartSelectActionType = "add" | "remove";

/** Event dispatch on select event is triggered */
export type SmartSelectOnSelectEvent<Value> = {
    /** New selected value  */
    selectedValue?: Value,

    /** 
     * Select or unselect item
     * Can be "remove" only if multiselection is enabled
     */
    actionType: SmartSelectActionType
}

/** Event dispatch on close event is triggered */
export type SmartSelectOnCloseEvent = {
    /** Indicates if selection was updated while the selector was opened */
    wasSelectionUpdated: boolean
}

/** Default item used by SmartSelect */
export interface DefaultSmartSelectItem {
    id: number;
    value: string;
}

/** Represent custom item for sections used in smartSelect */
export interface SmartSelectSectionItem<SectionUid extends string = string> {
    /** Uid of section */
    uid: SectionUid;

    /** Label of section if getter is defined in options */
    label: AcceptedHtmlElementContent | undefined;

    /** marker for section item type */
    __isSection: boolean;
}

export interface SmartSelectOptions<ItemType, Value, SectionUid extends string = string> extends FilterOptions<ItemType> {

    /** Data used by SmartSelect, the full list of items to display in <select> */
    data: ValueOrFunction<ItemType[]>;

    /** State of select, can be open or close */
    state: "open" | "close";

    /** Maximum count of rows to display in select (define smartList maxRows) */
    maxRowsDisplayed: number;

    /** Current selected values */
    selectedValue: Value | null;

    /** Text or Html to display when select is empty, add a "null" option to <select> */
    placeholderText?: AcceptedHtmlElementContent;

    /** Empty value to select */
    emptyValue?: DataEmptyValue<null>;

    /** Select is disabled */
    disabled: ValueOrFunction<boolean>;

    /**
     * Format a given item, to an HTMLElement or a string to display in <select> and selectedValuesList.
     * Will also be the smartList column's getCellValue
     * @param item Item of row
     * @param cell Cell of smartTable for item
     * @param p optional params
     * @param p.isSelectedValueRender true if format is for "smartSelectContainer" (selected value displayed). Use it if you want a different render for item when selected
     */
    itemFormatter?: (item: ItemType, cell?: SmartTableCellGetValue<ItemType>, p?: { isSelectedValueRender: boolean }) => AcceptedHtmlElementContent;

    /** Define stringify callback for cell value, will be used by filter if defined */
    getDisplayString?: (item: ItemType) => string;

    /** Callbacks to override to adapt SmartSelect with your desired behavior */
    callbacksToOverride?: {

        /** Get value of an item (often the 'id' of the item)  */
        getItemValue: (item: ItemType) => ValueType<Value> | undefined,

        /** 
         * Return the item from the itemList from its value
         * * default implementation uses getItemValue to find the match and is sufficient, most of the time you don't need to override this function
         */
        getItemByValue?: (items: ItemType[], value: ValueType<Value>) => ItemType | undefined
    };

    /**
     * Options used for multiselect, if enable is set to true, smartSelect will be a multi value selector
     */
    multiSelect?: {
        /** Enable MultiSelection */
        enable: boolean,

        /** 
         * Format a given selected item list to an HTMLElement or string to display in the smartSelectContainer
         * Replace itemFormatter for smartSelectContainer format when multi select is enabled
         * @param items Items to format
         */
        multiSelectionItemFormatter?: (items: ItemType[]) => AcceptedHtmlElementContent,

        /**
         * Count of max values displayed in multiSelectionItemFormatter
         * ! Applied only if "multiSelectionItemFormatter" isn't defined
         * * number: (ex: 3, with 6 names selected) will display: "name1, name2, name3 (+3)"
         * * null: will not truncate for maxValues, will add ellipsis if content overflow: "name1, name2, name3, name4,..."
         * @default 4
         */
        maxValuesCountDisplayedInFormatter?: number | null,

        /** All items will be selected when select is init */
        selectAllOnInit?: boolean
    };

    /**
     * SmartSelect popup options
     */
    popup?: Partial<SmartSelectPopupOptions> & {

        /** Enable width expand to the smartSelect's SmartList , to use it avoid any css global table width rule inside your component   */
        fitContent?: boolean
    };

    /** Height of rows in list, if not given, rowHeight value used is {this.clientHeight} */
    rowHeight?: number;

    /** If enable, select will stay open after user selection */
    keepSelectOpenAfterSelect?: boolean;

    /** If true, disable selection on item */
    disableItemSelection?: (item: ItemType) => boolean;

    /** 
     * Group items in separate sections 
     * ! Don't work with filter, sort, hierarchy and multiselect !
     */
    sections?: {
        /** Enable of disable section behavior */
        enable: boolean,
        /** Section getter, list of uid (ex: "Book" | "Objects" | "Items" | "Others") */
        data: ValueOrFunction<SectionUid[]>,

        /** Get label of section, optional. If label isn't given, display a simple line */
        getSectionLabel?: (sectionUid: SectionUid) => AcceptedHtmlElementContent,

        /** Get section of given item, if returns undefined, items are pushed at end of list displayed in select */
        getItemSection: (item: ItemType | null, itemValue: Value | undefined) => SectionUid | undefined,

        /**
         * Define height of section
         * @default 16
         */
        sectionHeight?: (sectionUid: SectionUid) => number
    };

    /** Prevent scroll on open, set to false to force scroll when opening in smart mode */
    preventScrollOnSelect?: boolean;

    /** If true do not clear hierarchy on select */
    doNotClearHierarchy?: boolean;
}

/** Parameters for SmartSelect */
export const defaultSmartSelectOptions: SmartSelectOptions<DefaultSmartSelectItem, string> = {

    /** Text or Html to display when select is empty */
    placeholderText: undefined,

    /** State of select, can be open or close */
    state: "close",

    /** Maximum count of rows to display in select (define smartList maxRows) */
    maxRowsDisplayed: 6,

    /** Default: emptyData */
    data: [],

    /** Disabled select */
    disabled: false,

    /** Current selected values */
    selectedValue: null,

    /** By default, scroll to selected value */
    preventScrollOnSelect: false
};

/** Theses smartList options field can't be overriden, self managed by smartSelect */
type OmittedSmartListOptions = "rowHeight" | "firstDisplayedItemIndex";

const defaultSmartListOptions: Partial<SmartListOptions> = {
    emptyStateParams: {
        enable: "always",
        noData: {
            options: { preConfig: EmptyStateDisplayPreConfig.NO_DATA }
        },
        noResultsInFilter: {
            options: { preConfig: EmptyStateDisplayPreConfig.NO_RESULT_IN_FILTER },
            enableClearFilter: false
        }
    }
};

@CustomElement({
    name: "cjs-smart-select",
    style: require("./smartSelect.scss"),
    template: require("./smartSelect.html"),
    i18n
})
export class CjsSmartSelect<ItemType, Value, SectionUid extends string = string> extends WebComponent<I18nType> {

    @TypedHtmlElements()
    public readonly components!: {
        smartSelectContainer: HTMLElement
    };

    @TypedWebComponentOptions(defaultSmartSelectOptions)
    public readonly options!: WebComponentOptions<SmartSelectOptions<ItemType, Value, SectionUid>>;

    /** get the value of disabled property of options  */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    /**
     * Used to override smartList options
     * ! "rowHeight" and "firstDIsplayedItemIndex" options are ommited
     */
    @TypedWebComponentOptions()
    public readonly smartListOptions!: WebComponentOptions<Omit<Partial<SmartListOptions<ItemType>>, OmittedSmartListOptions>>;

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @Event(NoBubblesEventInit)
    public selectEvent!: EventHandler<SmartSelectOnSelectEvent<Value>>;

    @Event(NoBubblesEventInit)
    public openEvent!: EventHandler;

    @Event(NoBubblesEventInit)
    public closeEvent!: EventHandler<SmartSelectOnCloseEvent>;

    /** SmartList of select null => empty value or no data*/
    public smartList = new CjsSmartList<ItemType | null>();

    /** ShowPopup where smartList is append */
    protected _popupSmartList?: ShowPopupReturn;

    /** Stash selected data on select opening. Is used for comparison with selection on select closing */
    private _selectedValueStash: Value | null = null;

    /** Row objects to display in select */
    protected _data: ItemType[] = [];

    /** Get accessor for data of smartSelect */
    public get data(): ItemType[] { return this._data; }

    /** true if smartList was opened once, used by "selectAllOnInit" option, to select all only on first refresh */
    public wasSmartListOpened = false;

    /** Smart select container height, to be passed to _getRowHeight function*/
    private _smartSelectContainerClientHeight = 16;

    /** Count of max values displayed by default multiselect formatter */
    protected readonly MULTISELECT_DEFAULT_MAX_VALUES_COUNT_DISPLAYED = 4;

    /** Property marker of isSection */
    protected readonly _sectionProperty = "__isSection";

    constructor(options?: Partial<SmartSelectOptions<ItemType, Value, SectionUid>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.smartListOptions.setWithoutRefresh(defaultSmartListOptions);
    }

    /**
     * init component
     */
    public override init(): void {
        this._initListeners();
    }

    /**
     * Refresh the view of the select from the selectedValue
     */
    public override refresh(): void {
        if (this.isMultiSelectEnable() && this.options.emptyValue) {
            this._log?.warn("options multiselect and emptyValue are not compatible, removing emptyValue option");
            this.options.setWithoutRefresh({ emptyValue: undefined });
        }

        this._data = this._readDataFromOptions();

        // Handle "selectAllOnInit" option: Select all items in first refresh only
        this._handleMultiSelectAllOnInit();

        this._refreshState();
        this._refreshDisabled();
        this._refreshPlaceholder();

        if (this._data.length) {
            const selectedValues = this._getSelectedValuesArray();
            if (selectedValues.length) {
                const itemsDisplayedInSelect: ItemType[] = [];
                for (const selectedValue of selectedValues) {
                    if (selectedValue != null) {
                        const item = this._getItemByValue(this._data, selectedValue);
                        if (item) {
                            itemsDisplayedInSelect.push(item);
                        } else {
                            this._log?.warn(`No item found for value: ${selectedValue}, look for errors into your getItemByValue function`);
                        }
                    }
                }

                if (itemsDisplayedInSelect.length) {
                    let formattedItem: AcceptedHtmlElementContent = "";
                    if (!this.isMultiSelectEnable()) {
                        formattedItem = this._itemFormatter(itemsDisplayedInSelect[0], undefined, { isSelectedValueRender: true });
                    } else {
                        formattedItem = this._multiSelectionItemFormatter(this.getSelectedItems() ?? []);
                    }

                    setHTMLElementContent(this.components.smartSelectContainer, formattedItem);
                } else if (this.options.emptyValue) {
                    this.options.setAndRefreshIfChanged({ selectedValue: this.options.emptyValue.value });
                }
            }
        }

        // In all cases, refresh smartList if popup is opened
        this.refreshSmartListDataIfNeeded();
    }

    /** Refresh data in smartList if already opened */
    public refreshSmartListDataIfNeeded(): void {
        if (!this.smartList || !this._popupSmartList) { return; }// Check if popup is still opened

        const listData: (ItemType | null)[] = [... this._data];
        if (this.options.emptyValue) {
            // EMPTY VALUE row
            listData.unshift(null);// insert null
        } else if (listData.length === 0) {
            // NO DATA row
            listData.push(null);
        }

        this.smartList.setData({ data: listData, clear: { selection: false, hierarchy: !Boolean(this.options?.doNotClearHierarchy) } });// Useless to clear selection, done by refreshSelectionInList
        this._refreshSelectionInList({ preventScroll: true });
        // Set top and left positions
        this._popupSmartList.popup.style.height = toPx(this._calcPopupElementHeight(this._data.length));
    }

    /** API: Open select */
    public open(): void {
        if (!this.isOpen() && !this._popupSmartList && !this.disabled) {
            this._onClickHandler();// "onOpen" will be called at end of clickHandler
        }
    }

    /** API: Close select */
    public close(): void {
        if (this.isOpen() && this._popupSmartList && !this.disabled) {
            this._popupSmartList.removePopup("close");// "onClose" will be called in "onCancel" and "onHide" of popup
        }
    }

    /**
     * @returns true if the select is open
     */
    public isOpen(): boolean {
        return this.options.state === "open";
    }

    /**
     * @returns data read from options and ensures not null
     */
    protected _readDataFromOptions(): ItemType[] {
        // If no data, return empty array
        if (this.options.data == null) {
            return [];
        }

        // If data is an Array(not Function), return it
        if (Array.isArray(this.options.data)) {
            return this.options.data;
        }

        // If data is a Function, return is call
        return this.options.data() ?? [];
    }

    /** Get selectedValues in array format with forced typing */
    protected _getSelectedValuesArray(): ValueType<Value>[] {
        return wrapAsArray(this.options.selectedValue) as ValueType<Value>[];
    }

    /** Refresh placeholder of select */
    protected _refreshPlaceholder(): void {
        let placeholderText: AcceptedHtmlElementContent = "";

        // Use placeholderText from options if exists, else use placeholders from i18n
        if (this.options.placeholderText != null) {
            placeholderText = this.options.placeholderText;
        } else if (this.i18n) {
            const noSelectedValuePlaceholder = !this.isMultiSelectEnable() ? this.i18n.noSelectedValuePlaceholder.default : this.i18n.noSelectedValuePlaceholder.plural;
            placeholderText = this._data.length ? noSelectedValuePlaceholder : this.i18n.noDataPlaceholder;
        }

        // Set placeholder
        setHTMLElementContent(this.components.smartSelectContainer, this.defaultPlaceholderFormatter(placeholderText));
    }

    /**
     * Format value in string format, to display it in <select> main div
     */
    public formatValueDisplay(item: ItemType | null): string {
        return String(this.getItemValue(item));
    }

    /**
     * Callback: Get value of an item (often the 'id' of the item) 
     */
    public getItemValue(item: ItemType | null): ValueType<Value> | undefined {
        if (item == null) {
            // empty value row
            return this.options.emptyValue?.value as anyReallyAny;
        }

        // For section items, no value
        if (this.isSectionItem(item)) {
            return undefined;
        }

        if (this.options.callbacksToOverride?.getItemValue) {
            return this.options.callbacksToOverride.getItemValue(item);
        }
        return (item as anyReallyAny).value as ValueType<Value>; // Cast necessary to compile, method will be overridden in most cases
    }

    /**
     * Callback: Get displayString of an item 
     * returns item value stringified if not defined in options
     */
    public getDisplayString(item: ItemType | null): string {
        if (this.options.getDisplayString && item != null) {
            return this.options.getDisplayString(item);
        }
        const value = this.getItemValue(item);
        return value != null ? String(value) : "";
    }

    /**
     * Default item string value formatter of smartSelect
     */
    public defaultItemStringFormatter(value: string): AcceptedHtmlElementContent {
        if (!value) { return ""; }

        const ellipsis = new CjsEllipsis(value);
        ellipsis.classList.add(FLEX_CONTAINER, ALIGN_ITEMS.CENTER, W_100, H_100);

        const itemDiv = createHtmlElement("div", {
            cssClass: [SMART_SELECT_ITEM_ELEMENT, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, W_100, FONT_WEIGHT.BOLD],
            innerHTML: ellipsis
        });
        return itemDiv;
    }

    /**
     * Default string value formatter of smartSelect for no data and empty
     */
    public defaultNoItemStringFormatter(value: string): AcceptedHtmlElementContent {
        if (!value) { return ""; }

        const ellipsis = new CjsEllipsis(value);
        ellipsis.classList.add(FLEX_CONTAINER, ALIGN_ITEMS.CENTER, W_100, H_100);

        const itemDiv = createHtmlElement("div", {
            cssClass: [SMART_SELECT_ITEM_ELEMENT, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, W_100, UNSELECTED_LIST_CLASS],
            innerHTML: ellipsis
        });
        return itemDiv;
    }

    /**
     * Default placeholder formatter of smartSelect
     */
    public defaultPlaceholderFormatter(value: AcceptedHtmlElementContent): AcceptedHtmlElementContent {
        if (!value) { return ""; }

        const ellipsis = new CjsEllipsis(value);
        ellipsis.classList.add(FLEX_CONTAINER, ALIGN_ITEMS.CENTER, W_100, H_100);
        const itemDiv = createHtmlElement("div", {
            cssClass: [SMART_SELECT_ITEM_ELEMENT, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, W_100],
            innerHTML: ellipsis
        });
        return itemDiv;
    }

    /**
     * Default section formatter of smartSelect
     */
    public defaultSectionFormatter(item: ItemType): AcceptedHtmlElementContent {
        if (!this.isSectionItem(item)) { return ""; }

        if (!item.label) {// If label isn't defined or empty, only display a single line separator
            return createHtmlElement("div", { cssClass: [W_100, SECTION_ITEM_CLASS] });
        }

        // If label is defined, display it in middle or row between two lines
        return createHtmlElement("div", {
            cssClass: [FLEX_CONTAINER, ALIGN_ITEMS.CENTER, W_100, TEXT_WRAPPING.NO_WRAP],
            innerHTML: [
                createHtmlElement("div", { cssClass: [FLEX_ITEM_AUTO, SECTION_ITEM_CLASS] }),
                item.label,
                createHtmlElement("div", { cssClass: [FLEX_ITEM_AUTO, SECTION_ITEM_CLASS] })
            ]
        });
    }


    /**
     * Item formatter of smartSelect
     * @param item Item to format
     * @param cell Cell of smartTable for item
     * @param p optional parameters
     * @param p.isSelectedValueRender true if format is for "smartSelectContainer" (selected value displayed)
     */
    protected _itemFormatter(item: ItemType | null, cell?: SmartTableCellGetValue<ItemType>, p?: { isSelectedValueRender: boolean }): HTMLElement | string {

        if (item == null) {
            if (this.options.emptyValue?.label) {
                return this.defaultNoItemStringFormatter(this.options.emptyValue?.label);
            } else if (this._data.length === 0) {
                return this.defaultNoItemStringFormatter(this.i18n.noDataPlaceholder);
            } else {
                return this.defaultNoItemStringFormatter("");
            }
        }

        // If section item, use default section formatter
        if (this.isSectionItem(item)) {
            return this.defaultSectionFormatter(item);
        }

        if (this.options.itemFormatter) {
            return this.options.itemFormatter(item, cell, p);
        }
        return this.defaultItemStringFormatter(this.formatValueDisplay(item));
    }

    /**
     * Default item formatter of smartSelectContainer on multiSelection
     * @param selectedItems Items to format
     */
    protected _multiSelectionItemFormatter(selectedItems: ItemType[]): AcceptedHtmlElementContent {
        if (this.options.multiSelect?.multiSelectionItemFormatter) {
            return this.options.multiSelect?.multiSelectionItemFormatter(selectedItems);
        }

        const maxValuesCount = this.options?.multiSelect?.maxValuesCountDisplayedInFormatter ?? this.MULTISELECT_DEFAULT_MAX_VALUES_COUNT_DISPLAYED;

        let labelToDisplay = "";
        if (selectedItems.length > maxValuesCount && this.options?.multiSelect?.maxValuesCountDisplayedInFormatter !== null) {
            labelToDisplay = selectedItems.slice(0, maxValuesCount)
                .map(i => this.formatValueDisplay(i)).join(", ") + ` (+${selectedItems.length - maxValuesCount})`;
        }
        else { labelToDisplay = selectedItems.map(i => this.formatValueDisplay(i)).join(", "); }


        const ellipsis = new CjsEllipsis(labelToDisplay);
        ellipsis.classList.add(FLEX_CONTAINER, ALIGN_ITEMS.CENTER, W_100, H_100);

        return createHtmlElement("div", {
            cssClass: [FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, W_100, FONT_WEIGHT.BOLD],
            innerHTML: ellipsis
        });


    }

    /**
     * Default getItemByValue method of SmartSelection
     */
    protected _getItemByValue(data: ItemType[], value: ValueType<Value> | undefined): ItemType | undefined {
        if (value != null && this.options.callbacksToOverride?.getItemByValue) {
            return this.options.callbacksToOverride.getItemByValue(data, value);
        }

        if (value == null || !data?.length) { return undefined; }

        return data.find((item: ItemType) => this.getItemValue(item) === value);
    }

    /** Init listeners of smartSelect */
    protected _initListeners(): void {
        this.components.smartSelectContainer.addEventListener("click", (e: Event) => this._onClickHandler(e));

        // Listen keyboard event press
        document.addEventListener(KeyboardEventType.UP, this._onKeyboardKeyUp);
    }

    /** When component is added to DOM */
    public connectedCallback(): void {
        if (this.isInitDone) {
            document.addEventListener(KeyboardEventType.UP, this._onKeyboardKeyUp);
        }
    }
    /** When component is removed from DOM */
    public disconnectedCallback(): void {
        document.removeEventListener(KeyboardEventType.UP, this._onKeyboardKeyUp);
    }

    /** Refresh disabled state */
    protected _refreshDisabled(): void {
        toggleAttribute(this, this.disabled, "disabled", "");
    }

    /**
     * Display a smartList with showPopup
     */
    // eslint-disable-next-line complexity
    protected _onClickHandler(handledClickEvent?: Event): void {

        if (!this.isOpen() && !this.disabled) {

            // Value to be used by default in getRowHeight of smartlist 
            this._smartSelectContainerClientHeight = this.components.smartSelectContainer.clientHeight;

            // we need to refresh data (for example re-read dataSource)
            this._data = this._readDataFromOptions();
            // Initialize smartList
            this.smartList = new CjsSmartList({
                columnsLayout: "fixed",
                rowHeight: item => this._getRowHeight(item),
                firstDisplayedItemIndex: this._getFirstSelectedItemIndex() ?? 0,
                disableHeader: Boolean(this.options.filterDisabled),
                columnsResizeDisabled: true,
                renderCell: cell => {
                    const item = this.smartList.getRowItemData(cell.row);
                    if (this.isSectionItem(item)) {// For section, return itemFormatter (cell.html is required for render)
                        // If section item, add custom class to rendered cell
                        cell?.html?.classList.add(SECTION_ITEM_TD_CLASS);
                        return this._itemFormatter(cell.rowItemData, cell);
                    }

                    return cell.value;
                },
                columns: [{
                    uid: SINGLE_COLUMN_UID,
                    filterDisabled: Boolean(this.options.filterDisabled),
                    filterComparator: this.options.filterComparator,
                    getDisplayString: cell => this.getDisplayString(cell.rowItemData),
                    getCellValue: (cell) => this._itemFormatter(cell.rowItemData, cell) // FIXME Utiliser le "getDisplayString" quand il sera ajouté à CervvalJs pour les SmartTable
                }],
                selection: {
                    selectionType: this.isMultiSelectEnable() ? SELECTION_TYPE.MULTIPLE : SELECTION_TYPE.SINGLE,
                    enableBackgroundDisplay: !this.isMultiSelectEnable(),// Don't display selection background in multiSelection, checkbox already perform selection state 
                    isRowSelectable: (row, item) => {

                        // Section items aren't selectable
                        if (this.isSectionItem(item)) { return false; }

                        // If item is disabled, row isn't selectable
                        if (this.options.disableItemSelection && this.options.disableItemSelection(item)) {
                            return false;
                        }

                        // Default isRowSelectable option
                        return this.smartListOptions.selection?.isRowSelectable?.(row, item) ?? true;
                    }
                },
                disableSort: true
            });

            // Override option with given in smartListOptions
            this.smartList.options.setWithoutRefresh(this.smartListOptions);


            if (this.isMultiSelectEnable()) {
                const checkBoxColumn = new SmartTableCheckboxSelectorColumn(this.smartList);
                checkBoxColumn.insertFirst();
                // Set width of selects column if multiselect enabled
                this.smartList.styleHelper.setColumnWidth(checkBoxColumn.uid, "40px");
            }

            // Set smartList data

            let listData: (ItemType | null)[] = [... this._data];
            if (this.options.emptyValue) {
                // EMPTY VALUE row
                listData.unshift(null);// insert null
            } else if (listData.length === 0) {
                // NO DATA row
                listData.push(null);
            }

            // If sections are enable, wrap items into given section
            if (this.options.sections?.enable) {
                const sections = getVOFValue(this.options.sections.data) ?? [];
                if (sections.length) {// Group items only if some sections are given
                    // Define sectionUid of items
                    const itemsBySectionUid = buildArrayMapWithFunc(listData, item => this.options.sections?.getItemSection(item, this.getItemValue(item)), { acceptNullKey: true });

                    // Empty data list, all rows will be pushed in
                    listData = [];

                    for (const section of sections) {
                        // Get label of section
                        const label = this.options.sections.getSectionLabel?.(section);
                        // Add custom itemSection into listData
                        listData.push({ uid: section, label, [this._sectionProperty]: true } as ItemType);

                        // Add items of current section
                        listData.push(...itemsBySectionUid.get(section) ?? []);
                    }

                    // Add all others items
                    listData.push(...itemsBySectionUid.get(undefined as unknown as SectionUid) ?? []);// FIXME AMT - Fix typing in buildArrayMapWithFunc (don't return 'undefined' in typing even the key can be undefined)
                }
            }

            this.smartList.setData({ data: listData });

            // Add selection listeners
            this.smartList.selectionChangeEvent.on(e => {
                const upToDateRowSelection = e.detail.newSelection;
                this._updateSelectedValuesBasedOnSelectedRows(upToDateRowSelection, "replace");
            });

            // Listen row click in order to close selector event when single selecting the already selected value
            this.smartList.rowClickEvent.on(e => { this._handleSingleSelectAlreadySelectedValue(); });

            // Listen to enter click in filter
            this.smartList.filterEnterKeyEvent.on((e) => {
                if (this.smartList.displayableRows.length === 1 && this.smartList.options.columns.length > 0) {
                    // if we press enter in filter and there is only one item we select it
                    const row = this.smartList.displayableRows[0];

                    const cell = this.smartList.getCellByRowAndColumn(row.uid, this.smartList.options.columns[0].uid);
                    if (!cell) { return; }// Should not append
                    const clickedItem: SmartListOnClickEvent<ItemType | null> = {
                        cell
                    };
                    const rowUids = this._getRowAndItsChildrenUids(clickedItem.cell.row.uid);
                    this._updateSelectedValuesBasedOnSelectedRows(rowUids, "merge");
                }
            });

            this.smartList.filterEvent.on(() => {
                if (this._popupSmartList?.popup) {
                    this._popupSmartList.popup.style.height = toPx(this._calcPopupElementHeight(this._data.length));
                }
            });

            // On after smartTable render
            this.smartList.renderFinishEvent.on(() => {
                toggleClass(this.smartList, this.smartList.scrollBar.style.display === Display.BLOCK, "scrollbar-visible");
                // Update smartList scrollbar width css attribute
                this.style.setProperty("--smartList-scrollbar-width", toPx(this.smartList.components.scrollbarContainer.clientWidth));

                // Allow arrow keys up/down and enter key events
                this.smartList.options.columns.forEach(c => c.filterInput?.options.setAndRefreshIfChanged({
                    doNotCaptureKeyEvents: [
                        KeyboardInput.ARROW_UP,
                        KeyboardInput.ARROW_DOWN,
                        KeyboardInput.ENTER
                    ]
                }));
            });

            // After smartList has init, set selectedValues in smartList and refresh his display
            this.smartList.onAfterInit = () => {
                this._refreshSelectionInList();
            };

            const smartSelectContainerClientWidth = this.components.smartSelectContainer.clientWidth;
            const popupWidth = toPx(smartSelectContainerClientWidth);

            const popupOptions = this.options.popup;
            const popupFitContent = Boolean(popupOptions?.fitContent);

            // Popup CSS
            const cssPopup: ShowPopupStyling = {
                style: {
                    ...defaultCssPopup.style,
                    minWidth: popupFitContent ? popupWidth : undefined,
                    width: popupFitContent ? undefined : popupWidth,
                    height: toPx(this._calcPopupElementHeight(this._data.length)),
                    ...popupOptions?.cssPopup?.style
                },
                cssClass: popupOptions?.cssPopup?.cssClass
            };

            // Set smartList height
            this.smartList.style.height = toPx(this._calcSmartListHeight(this._data.length));

            // Toggle classes for fitContent
            toggleClass(this.smartList.table, popupFitContent, W_AUTO);
            toggleClass(this.smartList.table, popupFitContent, MIN_WIDTH_100);
            toggleClass(this.smartList.table, popupFitContent, W_100);


            const popupParams: ShowPopupParams = {
                popupPosition: PopupPosition.BOTTOM_START,
                parent: this,
                ...this.options.popup,
                elementToShow: this.smartList,
                event: handledClickEvent,
                disablePopupInDialog: this.options.popup?.disablePopupInDialog !== false,
                popupMode: this.isMultiSelectEnable() ? "default" : undefined,
                modifiers: [
                    {
                        name: "flip",
                        options: {
                            fallbackPlacements: ["top", "bottom"]
                        }
                    },
                    ...this.options.popup?.modifiers ?? []
                ],
                onPopupCancel: () => {
                    this.options.popup?.onPopupCancel?.();
                    this._onClose();
                },
                onPopupHide: () => {
                    this.options.popup?.onPopupHide?.();
                    this._onClose();
                },
                cssPopup
            };

            // If other popupContainer is given popup will be displayed with position absolute 
            if (this.options.popup?.parent != null) {
                const pickerBounds = this.getBoundingClientRect();
                popupParams.absolutePosition = {
                    top: pickerBounds.top + pickerBounds.height,
                    left: pickerBounds.left
                };
            }

            this._popupSmartList = showPopup(popupParams);

            // If smartTable isn't smart, scroll it to selected row
            if (!this.smartList.isSmart()) {
                this.smartList.scrollToSelectedRow();
            }

            if (!this.options.filterDisabled) {
                this.focus({ preventScroll: true });
            }

            // Trigger onOpen behavior
            this._onOpen();
        }

        // Close popup if smartSelect is already open
        else if (this._popupSmartList && !this.disabled) {
            this._popupSmartList.removePopup("cancel");
        }
    }

    /** Select all displayed (filtered) items by default.
     * Use omitFilter to select all items
     */
    public selectAll(p?: { refresh?: boolean, dispatchEvent?: boolean, omitFilter?: boolean }): void {
        const selectedValues: ValueType<Value>[] = [];
        const dataToSelect = p?.omitFilter ? this.data : this.smartList.displayableRows.map((r) => getVOFValue(r.data));

        for (const item of dataToSelect) {
            if (item == null) { continue; }
            const val = this.getItemValue(item);
            if (val !== undefined) {
                selectedValues.push(val);
            }
        }
        this.options.setWithoutRefresh({ selectedValue: selectedValues as Value });

        if (p?.refresh !== false) {
            this.refresh();
        }

        if (p?.dispatchEvent !== false) {
            this.selectEvent.emit({ actionType: "add", selectedValue: selectedValues as Value });
        }
    }

    /** Handle select all on init, will select all items if "selectAllOnInit" option is true, only called on first refresh */
    protected _handleMultiSelectAllOnInit(): void {
        if (this.wasSmartListOpened || !this.options.multiSelect?.selectAllOnInit) { return; }// Check if selectAllOnInit option is defined
        this.wasSmartListOpened = true;
        // Select all items, without refresh
        this.selectAll({ refresh: false, dispatchEvent: false, omitFilter: true });
    }

    /** Handle special case : when selecting already selected value on single select */
    protected _handleSingleSelectAlreadySelectedValue(): void {
        if (this.isMultiSelectEnable()) { return; }
        // pop up is destroyed when selection is updated => pop up is already managed
        if (!this.isOpen() || !this._popupSmartList) { return; }
        const stashArray = wrapAsArray(this._selectedValueStash);
        const selectedValueArray = wrapAsArray(this.options.selectedValue);
        const wasSelectionUpdated = !arraysEqualIgnoreOrder(stashArray, selectedValueArray);
        // if selection is not updated, it means that pop up closing must be handled manually, as pop up selection was not updated 
        if (wasSelectionUpdated) { return; }
        this._onSelect({ actionType: "add", selectedValue: this.options.selectedValue ?? undefined });
        if (!this.options.keepSelectOpenAfterSelect) {
            this._popupSmartList.removePopup("close");
            this._onClose({ wasSelectionUpdated });
        }
    }

    /**
     * Refresh the selection in the smartList displayed in the popup
     */
    protected _refreshSelectionInList(options?: { preventScroll?: boolean }): void {

        const rowsToSelect: RowUid[] = [];

        const displayableRowsByValue = buildObjectMapWithFunc(this.smartList.displayableRows, r => this.getItemValue(this.smartList.getRowItemData(r)));

        // Add selected values of smartSelect in smartList selection model
        for (const selectedValue of this._getSelectedValuesArray()) {

            const item = this._getItemByValue(this._data, selectedValue);
            if (item == null) { continue; }

            const itemValue = this.getItemValue(item);
            if (itemValue == null) { continue; }

            const itemRowUid = displayableRowsByValue.get(itemValue)?.uid;

            // Add item to selection
            if (itemRowUid != null) {
                rowsToSelect.push(itemRowUid);
            }
        }

        // Select rows in smartList, if table is not smart we want to do a scrollIntoView (nativeScroll) after init
        this.smartList.selectRows(rowsToSelect, { add: true, preventScroll: options?.preventScroll || (this.smartList.isSmart() && this.options?.preventScrollOnSelect !== false), doNotRefreshIfScroll: true, doNotDispatchChangeEvent: true });
    }

    /** Get numbers of data rows to display */
    protected _getRowsCount(dataLength: number): number {
        let rowsCount = dataLength < this.options.maxRowsDisplayed ? dataLength : this.options.maxRowsDisplayed;
        if (this.options.emptyValue && dataLength < this.options.maxRowsDisplayed) { rowsCount++; }
        if (rowsCount === 0) {
            rowsCount = 1;// Handle no data
        }
        return rowsCount;
    }

    /** Calc smartList height */
    protected _calcSmartListHeight(dataLength: number): number {
        return (this._getRowHeight() + this.smartList.rowCssBordersHeight) * this._getRowsCount(dataLength);
    }

    /** Get rowHeight value to use in SmartList */
    protected _getRowHeight(item?: ItemType): number {
        if (item && this.isSectionItem(item)) {
            return this.options?.sections?.sectionHeight?.(item.uid) ?? 16;
        }
        return this.options.rowHeight ?? this._smartSelectContainerClientHeight;
    }

    /** Calc popupElement height */
    protected _calcPopupElementHeight(dataLength: number): number {

        // Get height of filter if enable
        const filterHeight = !Boolean(this.options.filterDisabled || this.smartListOptions.disableFilter) ? 46 : 0;// ! Fixed size !

        // Calc height of popup, based of rows count
        return this._calcSmartListHeight(dataLength) + filterHeight;
    }

    /** Get first item to display in smartlist, based on first selected value */
    protected _getFirstSelectedItemIndex(): number | undefined {
        const selectedValues = this._getSelectedValuesArray();
        const firstSelectedValue = selectedValues.sort(compare).at(0);
        const firstSelectedItem = firstSelectedValue != null ? this._getItemByValue(this._data, firstSelectedValue) : null;

        const dataSortedSorted = filterNotDefined(this.smartList.displayableRows.map(r => this.smartList.getRowItemData(r)));
        // Get first selectedItem
        let firstSelectedItemIndex = firstSelectedItem ? dataSortedSorted.indexOf(firstSelectedItem) : undefined;

        if (firstSelectedItemIndex != null) {

            const dataLength = dataSortedSorted.length;
            const rowsCount = this._getRowsCount(dataLength);

            // Center selected item
            firstSelectedItemIndex -= Math.round(rowsCount / 2);

            // Handle end of table
            if (dataLength - rowsCount < firstSelectedItemIndex) {
                firstSelectedItemIndex = dataLength - rowsCount;
            }

            // Handle start of table
            if (firstSelectedItemIndex < 0) {
                firstSelectedItemIndex = 0;
            }
        }
        return firstSelectedItemIndex;
    }

    /**
     * Get all heirs of given rowuids
     * @param rowUids
     * @returns given rowUids and their heirs
     */
    protected _getRowAndItsChildrenUids(rowUid: RowUid): RowUid[] {
        // returns only given row uid if hierarchy and multi selection options are not enabled 
        if (
            !this.smartListOptions.selection?.hierarchyParentSelectionWillSelectChildren ||
            !this.smartList.hierarchyHelper.parent.size ||
            !this.options.multiSelect?.enable
        ) { return [rowUid]; }

        const analysedRow = this.smartList.rowsByUid.get(rowUid);
        if (!analysedRow) { return [rowUid]; }
        const rowAndItsChildren = [analysedRow];
        // retrieve all heirs from a row and add their uids to the set
        this.smartList.hierarchyHelper.getAllChildrenRecursively(analysedRow, new Set(analysedRow.uid), rowAndItsChildren);
        const rowAndItsChildrenUids = rowAndItsChildren.map(row => row.uid);
        return rowAndItsChildrenUids;
    }

    /**
     * Update smartselect selected values with given row uids. Row uids can override current selection or be merged with it
     * @param selectedRowUidOrUids by click or by pressing enter
     * @param updateType for how we want to update selected values
     */
    protected _updateSelectedValuesBasedOnSelectedRows(selectedRowUidOrUids: RowUid | RowUid[], updateType: SmartSelectUpdateType): void {
        let actionType: SmartSelectActionType = "add";
        const isMultiSelectEnabled = this.isMultiSelectEnable();
        let selectedValuesSet: Set<ValueType<Value> | null>;
        if (!isMultiSelectEnabled || updateType === "replace") { selectedValuesSet = new Set(); }
        else { selectedValuesSet = new Set(this._getSelectedValuesArray()); }

        const selectedRowUids = wrapAsArray(selectedRowUidOrUids);

        for (const rowUid of selectedRowUids) {
            const row = this.smartList.rowsByUid.get(rowUid);
            if (!row) { continue; }

            const rowItem = this.smartList.getRowItemData(row);
            const itemValue = this.getItemValue(rowItem);

            if (rowItem != null && itemValue != null) {
                // If item is not selectable or a section, don't perform selection
                if (this.options.disableItemSelection?.(rowItem) || this.isSectionItem(rowItem)) { continue; }
                // Unselect values only if multi select is enabled
                if (isMultiSelectEnabled && selectedValuesSet.has(itemValue)) {
                    actionType = "remove";
                    selectedValuesSet.delete(itemValue);
                }
                else { selectedValuesSet.add(itemValue); }
            }

            // Multi selection and empty value options are incompatible : add empty value only when empty value option is on and multi selection option is off
            else if (this.options.emptyValue && !isMultiSelectEnabled) {
                selectedValuesSet.add(this.options.emptyValue.value);
            }
        }

        // Update selected value according to multi selection enabling state
        const selectedValuesList = [...selectedValuesSet];
        let selectedValue: Value | undefined;
        if (!isMultiSelectEnabled) {
            if (selectedValuesSet.size > 1) { getLogConsole().warn("Several values were selected on a single select. Only the first given value will be selected"); }
            selectedValue = selectedValuesList.at(0) ?? undefined;
        }
        else { selectedValue = selectedValuesList as Value; }

        this.options.setWithoutRefresh({ selectedValue });

        // Dispatch on select  (do not dispatch if no data)
        if (this._data.length > 0) {
            this._onSelect({ actionType, selectedValue: selectedValue });
        }

        // Close popup, only in single select mode
        if (this._popupSmartList && !isMultiSelectEnabled && !this.options.keepSelectOpenAfterSelect) {
            this._popupSmartList.removePopup("close");
        }

        // Finally, refresh
        this.refresh();
    }

    /** Callback when keyboard event is trigger */
    protected _onKeyboardKeyUp: EventListenerObject = {
        handleEvent: (e: KeyboardEvent) => {
            // Confirm with ENTER keypress
            if (this.isOpen() && (e.key === KeyboardInput.ARROW_DOWN || e.key === KeyboardInput.ARROW_UP)) {
                if (e.key === KeyboardInput.ARROW_UP) { this.smartList.selectPreviousRow({ doNotDispatchEvent: true }); }
                if (e.key === KeyboardInput.ARROW_DOWN) { this.smartList.selectNextRow({ doNotDispatchEvent: true }); }

                // ! Don't remove, compulsory to avoid scrolling of smartSelect container
                e.preventDefault();
                return;
            }

            if (this.isOpen() && e.key === KeyboardInput.ENTER) {
                const selectionModel = this.smartList.getSelectionModel();
                const row = this.smartList.tableRows.find(r => r.uid === selectionModel.leadSelectionRow);
                if (row) {
                    const rowUids = this._getRowAndItsChildrenUids(row.uid);
                    this._updateSelectedValuesBasedOnSelectedRows(rowUids, "merge");
                }


            }
            if (!this.isOpen() && e.key === KeyboardInput.ENTER && document.activeElement === this.components.smartSelectContainer) {
                this._onClickHandler();
            }
        }
    };

    /** On <select> is closed. Can pass on selection update state if computed beforehand */
    protected _onClose(p?: { wasSelectionUpdated: boolean }): void {
        this.options.setWithoutRefresh({ state: "close" });
        this._popupSmartList = undefined;
        // pass in close event whether selection was updated while opened
        let wasSelectionUpdated = p?.wasSelectionUpdated;
        if (wasSelectionUpdated === undefined) {
            const stashArray = wrapAsArray(this._selectedValueStash);
            const selectedValueArray = wrapAsArray(this.options.selectedValue);
            wasSelectionUpdated = !arraysEqualIgnoreOrder(stashArray, selectedValueArray);
        }
        this._selectedValueStash = null;
        this.closeEvent.emit({ wasSelectionUpdated });
        this._refreshState();
    }

    /** On <select> is opened */
    protected _onOpen(): void {
        this.options.setWithoutRefresh({ state: "open" });
        this._selectedValueStash = clone(this.options.selectedValue);
        this.openEvent.emit();
        this._refreshState();
    }

    /** Refresh attribute state based on state option of SmartSelect */
    protected _refreshState(): void {
        this.setAttribute("state", this.options.state);
    }

    /** On item is selected, dispatch event */
    protected _onSelect(params: SmartSelectOnSelectEvent<Value>): void {
        this.selectEvent.emit(params);
    }

    /** Return the list of selected items */
    public getSelectedItems(): ItemType[] | null {
        const items: ItemType[] = [];
        const selectedValues = this._getSelectedValuesArray();
        if (!selectedValues.length) { return null; }
        for (const val of selectedValues) {
            const item = this._getItemByValue(this._data, val);
            if (item) {
                items.push(item);
            }
        }
        return items;
    }

    /** Returns the first selected item  */
    public getFirstSelectedItem(): ItemType | undefined {
        return this.getSelectedItems()?.at(0);
    }

    /**
     * @returns The filter input if it exists
     */
    public getFilterInput(): HTMLElement | null {
        const input = this.smartList.querySelector("." + SEARCH_FILTER);
        return input as HTMLElement | null;
    }

    /**
     * Calls select on the input if available
     */
    public select(): void {
        const input = this.getFilterInput();
        if (input == null) { return; }

        if (typeof asAnyObject(input)["select"] === "function") {
            asAnyObject(input)["select"]();
        }
    }

    /**
     * Redirect focus on input
     */
    public override focus(options?: FocusOptions): void {
        const input = this.getFilterInput();
        input?.focus(options);
    }


    /**
     * Dispatch event on input
     */
    public dispatchEventOnInput(event: Event): boolean {
        return DomTools.dispatchEventOnInput(this.getFilterInput(), event);
    }

    /** @returns true if multiselect is enable */
    public isMultiSelectEnable(): boolean {
        return Boolean(this.options.multiSelect?.enable);
    }

    /** @returns true if item is a sectionItem */
    public isSectionItem(item: ItemType | SmartSelectSectionItem<SectionUid> | null): item is SmartSelectSectionItem<SectionUid> {
        return item && asAnyObject(item)[this._sectionProperty];
    }

}
